'use client';

import min from 'lodash/min';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import type { Product } from '@/queries/types/graphql';
import type { MessageKey } from '@/types';
import type { OrderLineItem } from '@/types/Order';

import { findProductDataBySku } from '../utils';

export interface ProductSelectData {
  currentPage: number;
  currentPageItems: OrderLineItem[];
  endNumber: number;
  isAllComplete: boolean;
  isFirstPage: boolean;
  isLastPage: boolean;
  isMiddlePage: boolean;
  isMultiPage: boolean;
  isMultiRing: boolean;
  isPageComplete: boolean;
  isSinglePage: boolean;
  isSingleRing: boolean;
  itemsPerPage: number;
  startIndex: number;
  startNumber: number;
  submitMsgKey: MessageKey;
  totalItemCount: number;
  totalPageCount: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

export interface UseProductSelectDataOptions {
  readonly firstPage?: number;
  readonly itemsPerPage?: number;
  readonly submitCompleteOrderMsgKey?: MessageKey;
  readonly submitContinueMsgKey?: MessageKey;
  readonly submitReviewOrderMsgKey?: MessageKey;
}

const useProductSelectData = (
  lineItems: OrderLineItem[],
  products: Product[],
  options: Partial<UseProductSelectDataOptions> = {},
): ProductSelectData => {
  const {
    firstPage = 1,
    itemsPerPage = 10,
    submitCompleteOrderMsgKey = 'complete_order',
    submitContinueMsgKey = 'continue',
    submitReviewOrderMsgKey = 'review_order',
  } = options;

  // helper function to determing if a lineItem is complete
  const getIsItemComplete = useCallback(
    ({ sku }: OrderLineItem) => {
      const { finish, size } = findProductDataBySku(products, sku);
      return Boolean(finish) && Boolean(size) && size !== 'Size later';
    },
    [products],
  );

  const [currentPage, setCurrentPage] = useState<number>(firstPage);

  // items
  const totalItemCount = lineItems?.length;
  const isMultiRing = totalItemCount > 1;
  const isSingleRing = totalItemCount === 1;

  // page count
  const totalPageCount = Math.ceil(totalItemCount / itemsPerPage);
  const isMultiPage = totalPageCount > 1;
  const isSinglePage = totalPageCount === 1;

  // relative page status
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPageCount;
  const isMiddlePage = !isFirstPage && !isLastPage;

  // ui elements
  const endNumber =
    min([currentPage * itemsPerPage, totalItemCount]) ?? totalItemCount;
  const startIndex = currentPage * itemsPerPage - itemsPerPage;
  const startNumber = startIndex + 1;
  const submitMsgKey: MessageKey = useMemo(() => {
    if (isMultiPage && isLastPage) {
      return submitReviewOrderMsgKey;
    }

    if (isMultiPage) {
      return submitContinueMsgKey;
    }

    return submitCompleteOrderMsgKey;
  }, [
    isLastPage,
    isMultiPage,
    submitContinueMsgKey,
    submitCompleteOrderMsgKey,
    submitReviewOrderMsgKey,
  ]);

  // completeness
  const currentPageItems = lineItems.slice(startIndex, endNumber);
  const isPageComplete = currentPageItems.every(getIsItemComplete);
  const isAllComplete = lineItems.every(getIsItemComplete);

  return {
    currentPage,
    currentPageItems,
    endNumber,
    isAllComplete,
    isFirstPage,
    isLastPage,
    isMiddlePage,
    isMultiPage,
    isMultiRing,
    isPageComplete,
    isSinglePage,
    isSingleRing,
    itemsPerPage,
    startIndex,
    startNumber,
    submitMsgKey,
    totalItemCount,
    totalPageCount,
    setCurrentPage,
  };
};

export default useProductSelectData;
